import { useState, useRef, useEffect } from "react";
import I from "./Icons";

interface DropdownProps {
  value: string;
  options: string[] | Array<{ v: string; l: string }>;
  onChange: (value: string) => void;
  label?: string;
  style?: React.CSSProperties;
  theme: any;
}

// ─── Dropdown ───
function Dropdown({ value, options, onChange, label, style: sx, theme }: DropdownProps) {
  const [open, setOpen] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const h = (e: MouseEvent) => { 
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); 
    };
    document.addEventListener("mousedown", h); 
    return () => document.removeEventListener("mousedown", h);
  }, []);
  
  const opts: Array<{ v: string; l: string }> = typeof options[0] === "string" ? (options as string[]).map(o => ({ v: o, l: o })) : options as Array<{ v: string; l: string }>;
  const sel = opts.find(o => o.v === value);
  
  return (
    <div ref={ref} style={{ position: "relative", ...sx }}>
      {label && (
        <div style={{ 
          fontSize: 11, 
          fontWeight: 700, 
          textTransform: "uppercase", 
          letterSpacing: ".09em", 
          color: theme.textSecondary, 
          marginBottom: 3 
        }}>
          {label}
        </div>
      )}
      <button 
        onClick={() => setOpen(!open)} 
        style={{ 
          width: "100%", 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "space-between", 
          padding: "6px 10px", 
          background: theme.cardBackground, 
          border: `1px solid ${open ? theme.blue : theme.border}`, 
          borderRadius: 6, 
          color: theme.text, 
          fontSize: 13, 
          cursor: "pointer", 
          gap: 4
        }}
      >
        <span style={{ 
          overflow: "hidden", 
          textOverflow: "ellipsis", 
          whiteSpace: "nowrap" 
        }}>
          {sel?.l || value}
        </span>
        <I.Down />
      </button>
      {open && (
        <div style={{ 
          position: "absolute", 
          top: "100%", 
          left: 0, 
          right: 0, 
          marginTop: 3, 
          background: theme.cardBackground, 
          border: `1px solid ${theme.border}`, 
          borderRadius: 8, 
          boxShadow: "0 8px 32px rgba(0,0,0,.25)", 
          zIndex: 1000, 
          maxHeight: 200, 
          overflowY: "auto", 
          padding: 3 
        }}>
          {opts.map(o => (
            <div 
              key={o.v} 
              onClick={() => { 
                onChange(o.v); 
                setOpen(false); 
              }} 
              style={{ 
                padding: "5px 9px", 
                borderRadius: 5, 
                cursor: "pointer", 
                fontSize: 13, 
                color: o.v === value ? theme.blue : theme.text, 
                fontWeight: o.v === value ? 600 : 400, 
                background: o.v === value ? `${theme.blue}20` : "transparent" 
              }}
              onMouseEnter={e => { 
                if (o.v !== value) e.currentTarget.style.background = theme.hoverBackground; 
              }} 
              onMouseLeave={e => { 
                if (o.v !== value) e.currentTarget.style.background = "transparent"; 
              }}
            >
              {o.l}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Dropdown;
